import { ActiveMediaFailedCode } from '@zoom/videosdk';

import { logger } from '../../../datadog/logger';
import type { MediaRecoveryEvent, RecoveryMediaType } from '../../core/types';
import { isWebGLSupported } from '../../core/webgl';

type MediaRecoveryKind = MediaRecoveryEvent['kind'];
type MediaFailureCategory = Exclude<MediaRecoveryKind, 'graphicsUnavailable'>;

/**
 * Single source of truth mapping every active-media-failed code to a recovery
 * category. Recovery semantics per category:
 * - interrupted: the SDK restores the media on any user gesture
 * - blocked: the user must grant camera/microphone permission
 * - failed: a page refresh recovers the session
 * - corrupted: the user must fully close and reopen the browser
 *
 * https://developers.zoom.us/docs/video-sdk/web/handle-events/#active-media-failed
 */
const MEDIA_FAILURE_CATEGORY: Record<ActiveMediaFailedCode, MediaFailureCategory> = {
  // audio errors
  [ActiveMediaFailedCode.AudioConnectionFailed]: 'failed', // 101
  [ActiveMediaFailedCode.AudioStreamEnded]: 'failed', // 102
  [ActiveMediaFailedCode.MicrophonePermissionReset]: 'blocked', // 103
  [ActiveMediaFailedCode.AudioStreamFailed]: 'corrupted', // 104
  [ActiveMediaFailedCode.MicrophoneMuted]: 'blocked', // 105
  [ActiveMediaFailedCode.AudioStreamMuted]: 'interrupted', // 106
  [ActiveMediaFailedCode.AudioPlaybackInterrupted]: 'interrupted', // 107
  // video errors
  [ActiveMediaFailedCode.VideoConnectionFailed]: 'failed', // 201
  [ActiveMediaFailedCode.VideoStreamEnded]: 'failed', // 202
  [ActiveMediaFailedCode.CameraPermissionReset]: 'blocked', // 203
  [ActiveMediaFailedCode.WebGlContextInvalid]: 'failed', // 204
  [ActiveMediaFailedCode.WasmOutOfMemory]: 'corrupted', // 205
  [ActiveMediaFailedCode.VideoStreamFailed]: 'corrupted', // 206
  [ActiveMediaFailedCode.VideoStreamMuted]: 'interrupted', // 207
  // screen share errors
  [ActiveMediaFailedCode.SharingStreamFailed]: 'corrupted', // 301
};

// a refresh restores a lost WebGL context, but not when the browser cannot create one at all
// (e.g. graphics acceleration is turned off); the user must re-enable it and relaunch the browser
function isGraphicsUnavailable(code: ActiveMediaFailedCode): boolean {
  return code === ActiveMediaFailedCode.WebGlContextInvalid && !isWebGLSupported();
}

function resolveRecoveryKind(code: ActiveMediaFailedCode): MediaRecoveryKind {
  return isGraphicsUnavailable(code) ? 'graphicsUnavailable' : MEDIA_FAILURE_CATEGORY[code];
}

/**
 * Maps an active-media-failed code to the recovery prompt it warrants, using the
 * exhaustive MEDIA_FAILURE_CATEGORY table.
 */
export function categorizeActiveMediaFailure(
  code: ActiveMediaFailedCode,
  message: string,
  type: RecoveryMediaType,
): MediaRecoveryEvent {
  const kind = resolveRecoveryKind(code);
  switch (kind) {
    case 'interrupted':
    case 'blocked':
    case 'graphicsUnavailable':
      logger.warn(`[ZoomEventBus] active media failed, ${kind}`, { code, message, type });
      return { kind, type };
    case 'failed':
    case 'corrupted':
      logger.error(`[ZoomEventBus] active media failed, ${kind}`, { code, message, type });
      return { kind, type, message };
    default: {
      logger.error(`[ZoomEventBus] active media failed, ${kind}`, { code, message, type });
      // unreachable for known SDK codes
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}
